import { P, H1, H2, H3, PrimaryButton, Spinner } from "@itpi/react-components";
import React, { useContext, useEffect, useState } from "react";

import AuthContext from "../context/AuthContext";
import { Page } from "../components/Page";

import { ScoreSlider } from "../components/ScoreSlider";
import { Justify } from "../components/Justify";
import {
  GetSelfAssessmentForm,
  SendSelfAssessment,
  GetSelfAssessments,
  GetSelfAssessmentCache,
  SendSelfAssessmentCache,
} from "../data/QuestionApi";
import { AnimatePresence, motion } from "framer-motion";
import { useHistory } from "react-router-dom";
import loadingbanner from "../assets/load.svg";
import useDebounce from "../hooks/debounce";

export const BaseURL = `${process.env.REACT_APP_API_BASE_URL}/entrecomp`;

type Answer = {
  competencyId: number;
  score?: number;
  answerText?: string;
};

const SelfAssessment: React.FC<{}> = () => {
  const { token, groups } = useContext(AuthContext);
  const [selfAssessmentForm, setSelfAssessmentForm] = useState<any>([]);
  const [formResponse, setFormResponse] = useState<Answer[]>([]);
  const [previousForm, setPreviousForm] = useState<any>({});
  const [initialCacheResponse, setInitialCacheResponse] = useState<Answer[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const history = useHistory();
  const [valid, setValid] = useState<boolean>(false);
  const debouncedFormResponse = useDebounce(formResponse, 3000);
  const [isSavingCache, setIsSavingCache] = useState<boolean>(false);

  useEffect(() => {
    if (debouncedFormResponse) {
      setIsSavingCache(true);
      if (
        debouncedFormResponse.length > 0 &&
        debouncedFormResponse !== initialCacheResponse
      ) {
        SendSelfAssessmentCache(token, debouncedFormResponse).finally(() => {
          //do this regardless of success or failure

          setIsSavingCache(false);
        });
      }
    }
  }, [debouncedFormResponse, initialCacheResponse, token]);

  useEffect(() => {
    setLoading(true);
    const formPromise = GetSelfAssessmentForm(token);

    const historyPromise = GetSelfAssessments(token);

    const cachePromise = GetSelfAssessmentCache(token);
    // synchronise api calls
    Promise.all([formPromise, historyPromise, cachePromise]).then((values) => {
      setSelfAssessmentForm(values[0]);
      if (values[1].length) {
        values[1].sort((a: any, b: any) => {
          return (
            new Date(b.dateCreated).getTime() -
            new Date(a.dateCreated).getTime()
          );
        });
        setPreviousForm(values[1][0]);
      }
      if (values[2] !== null) {
        //if cache returns data, populate inputs with cache
        setFormResponse(values[2]);
        setInitialCacheResponse(values[2]);
      }

      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const flattenedForm = selfAssessmentForm
      .map((area: any) => area.competencies)
      .flat();

    let valid = true;
    if (flattenedForm.length === formResponse.length) {
      formResponse.forEach((response) => {
        if (
          response.answerText === null ||
          response?.answerText === undefined ||
          response?.answerText.length === 0 ||
          response.score === null ||
          response.score === undefined
        ) {
          valid = false;
        }
      });
    } else {
      valid = false;
    }

    setValid(valid);
  }, [formResponse, selfAssessmentForm]);

  const setFormState = (
    competencyId: number,
    data: { score?: number; answerText?: string }
  ) => {
    const index = formResponse.findIndex((x) => {
      return x.competencyId === competencyId;
    });
    if (index === -1) {
      setFormResponse((old) => {
        let newState = [...old];
        newState.push({ competencyId: competencyId, ...data });

        return [...newState];
      });
    } else {
      setFormResponse((old) => {
        let newState = [...old];
        newState[index] = { ...newState[index], ...data };
        return [...newState];
      });
    }
  };

  const getFormState = (competencyId: number) => {
    const index = formResponse.findIndex((x) => {
      return x.competencyId === competencyId;
    });
    if (index === -1) {
      return null;
    } else {
      return formResponse[index];
    }
  };

  const sendAssessment = () => {
    setSubmitted(true);
    console.log("submit");
    SendSelfAssessment(token, formResponse).then((result) => {
      console.log(result);
      if (result.id !== undefined) {
        history.push(`/result/${result.id}`);
      } else {
        console.error("error:", result);
      }
    });
  };

  if (loading) {
    return (
      <Page>
        <H1>Complete your ECHO self-assessment</H1>
        <img
          src={loadingbanner}
          width="90%"
          style={{
            margin: "5%",
          }}
          height="400px"
          alt="banner for loading screen"
        />
        <P style={{ textAlign: "center" }}>Building the form...</P>
      </Page>
    );
  }

  const colors = ["#DCFCE7", "#E0F2FE", "#FEF9C3"];

  return (
    <Page>
      {isSavingCache ? (
        <div style={{ position: "fixed", bottom: 5, right: 5 }}>
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <P>Saving...</P>
            </motion.div>
          </AnimatePresence>
        </div>
      ) : null}
      <H1>Complete your ECHO self-assessment</H1>

      {/* loop for all questions */}

      {selfAssessmentForm.length
        ? selfAssessmentForm.map((area: any, index: number) => {
            return (
              <div key={index}>
                <H2
                  style={{
                    fontSize: 30,
                    textAlign: "center",
                    fontStyle: "italic",
                    fontWeight: "normal",
                  }}
                >
                  {area.name}
                </H2>
                <div
                  style={{
                    backgroundColor: colors[index % colors.length],
                    padding: 20,
                    marginBottom: 20,
                    borderRadius: 15,
                  }}
                >
                  {area.competencies.map((competency: any) => {
                    const previousAnswer =
                      previousForm?.answers?.filter(
                        (x: any) => x.competency.id === competency.id
                      )[0] || null;

                    return (
                      <div key={competency.name}>
                        <H3>{competency.name}</H3>
                        <P>I can...</P>
                        <ul style={{ marginBottom: 20 }}>
                          {competency.questions.map(
                            (question: any, index: number) => (
                              <li
                                key={index}
                                style={{
                                  marginBottom: 5,
                                  fontSize: 18,
                                  maxWidth: "80ch",
                                }}
                              >
                                {question.question}
                              </li>
                            )
                          )}
                        </ul>

                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <Justify
                            previousAnswer={previousAnswer?.answerText || null}
                            value={
                              getFormState(competency.id)?.answerText || ""
                            }
                            setText={(text: string) =>
                              setFormState(competency.id, { answerText: text })
                            }
                          />
                          <ScoreSlider
                            initialScore={previousAnswer?.score}
                            value={getFormState(competency.id)?.score}
                            setScore={(score: number) =>
                              setFormState(competency.id, { score: score })
                            }
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        : null}

      <PrimaryButton
        style={{ flexGrow: 0, margin: 10 }}
        onClick={() => {
          sendAssessment();
        }}
        disabled={!valid}
      >
        Submit{" "}
      </PrimaryButton>
      {submitted ? (
        <div style={{ display: "inline-block" }}>
          <Spinner />
        </div>
      ) : null}
      {!valid ? (
        <P style={{ color: "#b5121b" }}>
          Please ensure you have given a score and a justification for each
          competency
        </P>
      ) : null}
    </Page>
  );
};

export default SelfAssessment;
